<template>
  <div>
    <CreateJiraIssueForm
      v-if="isJiraApp"
      ref="createJiraIssueForm"
      :source="source"
      @save="onCreateJiraIssueFormSave"
    />

    <CreateWorkItemForm
      v-if="isAzureApp"
      ref="createWorkItemForm"
      :source="source"
      @save="onCreateWorkItemFormSave"
    />

    <portal to="modal-windows">
      <ObjectiveModal
        v-if="showObjectiveModal"
        ref="objectiveModal"
        :child-parameters="listState.requestParameters"
        :children-order="listState.requestParameters.order"
        :source="source"
        @close="onObjectiveModalClose"
        @closed="showObjectiveModal = false"
        @element-created="showElementCreatedNotification"
      />

      <ObjectiveDeleteModal
        v-if="isConfirmDeleteShow"
        v-model:show="isConfirmDeleteShow"
        :objective="deleteObjectiveData"
        :source="source"
        @deleted="onObjectiveDeleted"
        @update:show="!$event ? (deleteObjectiveData = null) : undefined"
      />

      <ObjectiveModalMoveStepper
        v-if="showModalMove"
        v-model:show="showModalMove"
        :objective-data="modalMoveData"
        @updated="onMoveObjective"
      />

      <AddToCustomDashboardModal
        :dashboards-list="dashboardsForAddElements"
        :opened="isShowBulkAddToDashboardModal"
        :selected-element-ids="elementIdsToAdd"
        @on-close="closeAddToCustomDashboardModal"
      />

      <CheckinModal
        v-if="isShowCheckinModal"
        v-model="isCheckinModalOpen"
        :entry-point="checkinModalLaunchData.entryPoint"
        :okr-element="checkinModalLaunchData.okrElement"
        :tracking-source="checkinTrackingSource"
        :view-config="listState.viewConfig"
        @saved="$emit('checkin-saved', $event)"
        @after-leave="onCheckinModalClosed"
      />
    </portal>
  </div>
</template>

<script>
import { cloneDeep } from 'lodash'
import { defineComponent, ref } from 'vue'

import { EVENT_SOURCES } from '@/tracking/amplitude-helpers'
import { useCheckinModal } from '@/utils/check-ins/check-ins'
import { useBulkAddToCustomDashboard } from '@/utils/custom-dashboard-helper'
import { OKR_ELEMENT_ENTITY_KEYS } from '@/utils/entity-keys'
import {
  isAzureAppInjectionKey,
  isJiraAppInjectionKey,
  listStateInjectionKey
} from '@/utils/injection-keys'
import { isKR, isWorkItem, OBJECTIVE_TYPES, objectiveIsJiraTask } from '@/utils/objective-types'
import {
  saveUpdatedElementParameters,
  currentUserCanCopyObjective,
  OKR_FORM_VIEWS,
  okrElementToLinkIssuePayload,
  OKR_ELEMENTS_TABLE_ADD_MENU_ITEMS,
  okrElementToLinkWorkItemPayload
} from '@/utils/objectives'
import {
  DEFAULT_MILESTONES_SETTINGS,
  DEFAULT_RESULT_TYPE_VALUES,
  isBinaryProgress,
  isStartTargetProgress,
  isThresholdProgress,
  METRIC_VALUES_KEYS
} from '@/utils/okr-element-values'
import { FILTERS_KEYS } from '@/utils/okr-elements/filters'
import { useObjectiveModalNotifications } from '@/utils/okr-elements/notifications'
import { numberOrNullProp } from '@/utils/prop-validators'
import { createEditOkrElementPayload } from '@/utils/view-row-actions'

import AddToCustomDashboardModal from '@/components/objectives/bulk-edit/AddToCustomDashboardModal'
import CheckinModal from '@/components/objectives/element-check-in/CheckinModal'
import ObjectiveModalMoveStepper from '@/components/objectives/objective-move/ObjectiveModalMoveStepper'
import ObjectiveDeleteModal from '@/components/objectives/ObjectiveDeleteModal'
import ObjectiveModal from '@/components/objectives/ObjectiveModal'
import CreateJiraIssueForm from '@jira/components/objectives/forms/CreateJiraIssue'
import CreateWorkItemForm from '@jira/components/objectives/forms/CreateWorkItemForm'

export default defineComponent({
  name: 'ObjectiveViewRowActions',

  components: {
    CreateWorkItemForm,
    CheckinModal,
    AddToCustomDashboardModal,
    ObjectiveModal,
    CreateJiraIssueForm,
    ObjectiveDeleteModal,
    ObjectiveModalMoveStepper
  },

  inject: {
    listState: {
      from: listStateInjectionKey
    },

    isJiraApp: {
      from: isJiraAppInjectionKey
    },

    isAzureApp: {
      from: isAzureAppInjectionKey
    }
  },

  props: {
    source: {
      type: String,
      default: ''
    },

    depth: {
      required: true,
      validator: v => numberOrNullProp(v)
    }
  },

  emits: { update: null, 'checkin-saved': null },

  setup() {
    const {
      proceedAddToDashboard,
      dashboardsForAddElements,
      isShowBulkAddToDashboardModal,
      closeAddToCustomDashboardModal,
      elementIdsToAdd
    } = useBulkAddToCustomDashboard()

    const handleAddToCustomDashboard = async element => {
      await proceedAddToDashboard([element[OKR_ELEMENT_ENTITY_KEYS.ID]])
    }

    const {
      isCheckinModalOpen,
      handleOpenCheckinModal,
      checkinModalLaunchData,
      isShowCheckinModal,
      onCheckinModalClosed
    } = useCheckinModal()

    const isModalOpen = ref(false)

    const { showModalCloseNotifications, showElementCreatedNotification } =
      useObjectiveModalNotifications({ isModalOpen })

    return {
      handleAddToCustomDashboard,
      dashboardsForAddElements,
      isShowBulkAddToDashboardModal,
      closeAddToCustomDashboardModal,
      elementIdsToAdd,
      isCheckinModalOpen,
      isShowCheckinModal,
      handleOpenCheckinModal,
      checkinModalLaunchData,
      onCheckinModalClosed,
      showObjectiveModal: isModalOpen,
      showModalCloseNotifications,
      showElementCreatedNotification
    }
  },

  data() {
    return {
      isConfirmDeleteShow: false,
      deleteObjectiveData: null,
      linkJiraTaskData: null,
      linkWorkItemData: null,
      showModalMove: false,
      modalMoveData: null
    }
  },

  computed: {
    checkinTrackingSource() {
      if (this.source === EVENT_SOURCES.TABLE) {
        const normalizedSourceName = this.listState.view.replace('OKR', '')
        return normalizedSourceName.toLowerCase()
      }

      return this.source
    }
  },

  methods: {
    onMoveObjective() {
      this.$emit('update')
    },

    onCreateEnvironmentPlatformEntityFormSave(objectives) {
      const blinkOkrElementIds = []

      objectives.forEach(okrElementInfo => {
        okrElementInfo.elements.forEach(element => {
          blinkOkrElementIds.push({
            uniqueId: element.uniqueId,
            onFirstLevel: false
          })

          const { parentId } = element
          const id = `${parentId}-${this.depth}`
          // new value is saved automatically after data update
          this.listState.filtersValues[FILTERS_KEYS.EXPANDED_ITEMS][id] = true

          saveUpdatedElementParameters(this.listState, okrElementInfo.updatedElementParameters)
        })
      })

      this.$emit('update', {
        blinkObjectiveIds: blinkOkrElementIds,
        keepObjectivesSize: true
      })
    },

    onCreateJiraIssueFormSave(objectives) {
      this.onCreateEnvironmentPlatformEntityFormSave(objectives)
    },

    onCreateWorkItemFormSave(objectives) {
      this.onCreateEnvironmentPlatformEntityFormSave(objectives)
    },

    onObjectiveModalClose(eventData) {
      this.showModalCloseNotifications(eventData, { onOpen: this.editOkrElement })

      const parameters = {
        keepObjectivesSize: true
      }

      const createdObject = eventData.createdObjective || eventData.createdKR

      const handleOkrElement = element => {
        parameters.blinkObjectiveIds = [
          {
            uniqueId: element.uniqueId,
            onFirstLevel: false
          }
        ]

        const { parentId } = element
        const id = `${parentId}-${this.depth}`
        // new value is saved automatically after data update
        this.listState.filtersValues[FILTERS_KEYS.EXPANDED_ITEMS][id] = true
      }

      if (createdObject) {
        handleOkrElement(createdObject)
      } else if (eventData.createdTasks) {
        eventData.createdTasks.forEach(issue => handleOkrElement(issue))
      } else if (eventData.createdWorkItems) {
        eventData.createdWorkItems.forEach(workItem => handleOkrElement(workItem))
      }

      this.$emit('update', parameters)
    },

    onObjectiveDeleted() {
      this.$emit('update', { keepObjectivesSize: true })

      if (this.showObjectiveModal) {
        this.$refs.objectiveModal.updateObjectiveChildObjectives()
      }
    },

    async linkTask() {
      this.showObjectiveModal = true
      await this.$nextTick()
      this.$refs.objectiveModal.openForm(
        OKR_FORM_VIEWS.LINK_JIRA_ISSUE,
        this.linkJiraTaskData,
        'grid'
      )
    },

    async linkWorkItem() {
      this.showObjectiveModal = true
      await this.$nextTick()
      this.$refs.objectiveModal.openForm(
        OKR_FORM_VIEWS.LINK_WORK_ITEM,
        this.linkWorkItemData,
        'grid'
      )
    },

    /* Public API */
    async createNewChildObjective(parentObjective, typeId, levelId) {
      this.showObjectiveModal = true
      await this.$nextTick()
      this.$refs.objectiveModal.openForm(
        OKR_FORM_VIEWS.OBJECTIVE,
        {
          typeId,
          intervalId: parentObjective.intervalId,
          parentId: parentObjective.id,
          workspaceId: parentObjective.workspaceId,
          depth: this.depth,
          automaticElementStartDate: parentObjective.automaticElementStartDate,
          automaticDueDate: parentObjective.automaticDueDate,
          intervalStartDate: parentObjective.intervalStartDate,
          intervalEndDate: parentObjective.intervalEndDate,
          levelId
        },
        'grid'
      )
    },

    /* Public API */
    async createNewKR(parentOkrElement, levelId) {
      this.showObjectiveModal = true
      await this.$nextTick()

      this.$refs.objectiveModal.openForm(
        OKR_FORM_VIEWS.KR,
        {
          typeId: OBJECTIVE_TYPES.KR,
          levelId,
          intervalId: parentOkrElement.intervalId,
          parentId: parentOkrElement.id,
          workspaceId: parentOkrElement.workspaceId,
          depth: this.depth,
          automaticElementStartDate: parentOkrElement.automaticElementStartDate,
          automaticDueDate: parentOkrElement.automaticDueDate,
          intervalStartDate: parentOkrElement.intervalStartDate,
          intervalEndDate: parentOkrElement.intervalEndDate
        },
        'grid'
      )
    },

    /** @public */
    moveOkrElement(payload) {
      this.showModalMove = true
      this.modalMoveData = payload
    },

    /** @public */
    addToCustomDashboard(element) {
      this.handleAddToCustomDashboard(element)
    },

    /** @public */
    createNewOkrElement(parentObjective, payload) {
      if (payload.typeId === OBJECTIVE_TYPES.PERSONAL) {
        this.createNewChildObjective(parentObjective, payload.typeId, payload.levelId)
      } else if (isKR(payload)) {
        this.createNewKR(parentObjective, payload.levelId)
      } else if (objectiveIsJiraTask(payload) && payload.action === 'link') {
        this.askForLinkJiraIssue(parentObjective)
      } else if (
        objectiveIsJiraTask(payload) &&
        payload.action === 'create' &&
        this.$refs.createJiraIssueForm
      ) {
        this.$refs.createJiraIssueForm.create(parentObjective)
      } else if (payload.workItemKindId && this.$refs.createWorkItemForm) {
        this.$refs.createWorkItemForm.create(parentObjective, payload)
      } else if (payload.action === OKR_ELEMENTS_TABLE_ADD_MENU_ITEMS.LINK_WORK_ITEM.name) {
        this.askForLinkWorkItem(parentObjective)
      }
    },

    askForLinkJiraIssue(parentObjective) {
      this.linkJiraTaskData = okrElementToLinkIssuePayload(parentObjective)
      this.linkTask()
    },

    askForLinkWorkItem(parentObjective) {
      this.linkWorkItemData = okrElementToLinkWorkItemPayload(parentObjective)
      this.linkWorkItem()
    },

    /** @public */
    async editOkrElement(element, editPayload) {
      let payload = createEditOkrElementPayload(element)

      if (isKR(element)) {
        const {
          parentId,
          originalValue,
          currentValue,
          targetValue,
          fieldTypeId,
          unit,
          thresholdConditionTypeId,
          elementTargetPeriods = cloneDeep(DEFAULT_MILESTONES_SETTINGS.elementTargetPeriods),
          periodAggregationTypeId = DEFAULT_MILESTONES_SETTINGS.periodAggregationTypeId,
          progressAggregationTypeId = DEFAULT_MILESTONES_SETTINGS.progressAggregationTypeId,
          granulationId = DEFAULT_MILESTONES_SETTINGS.granulationId
        } = element

        payload = {
          ...payload,
          elementTargetPeriods,
          periodAggregationTypeId,
          progressAggregationTypeId,
          granulationId,
          parentId,
          originalValue,
          currentValue,
          targetValue,
          fieldTypeId,
          thresholdConditionTypeId:
            thresholdConditionTypeId || DEFAULT_RESULT_TYPE_VALUES.thresholdConditionTypeId,

          unit
        }

        if (isStartTargetProgress({ element })) {
          payload = {
            ...payload,
            [METRIC_VALUES_KEYS.NUMBER_ORIGINAL_VALUE]: originalValue,
            [METRIC_VALUES_KEYS.NUMBER_CURRENT_VALUE]: currentValue,
            [METRIC_VALUES_KEYS.NUMBER_TARGET_VALUE]: targetValue
          }
        }

        // if (isNumberWithUnitProgress({ element })) {
        //   payload = {
        //     ...payload,
        //     percentOriginalValue: originalValue,
        //     percentCurrentValue: currentValue,
        //     percentTargetValue: targetValue
        //   }
        // }

        if (isThresholdProgress({ element })) {
          payload = {
            ...payload,
            [METRIC_VALUES_KEYS.THRESHOLD_ORIGINAL_VALUE]: originalValue,
            [METRIC_VALUES_KEYS.THRESHOLD_CURRENT_VALUE]: currentValue,
            [METRIC_VALUES_KEYS.THRESHOLD_TARGET_VALUE]: targetValue
          }
        }

        if (isBinaryProgress({ element })) {
          payload[METRIC_VALUES_KEYS.BINARY_VALUE] = currentValue
        }

        this.showObjectiveModal = true
        await this.$nextTick()
        this.$refs.objectiveModal.openForm(OKR_FORM_VIEWS.KR, payload, 'grid', false, editPayload)
      } else if (objectiveIsJiraTask(element)) {
        this.linkJiraTaskData = element
        this.linkTask()
      } else if (isWorkItem(element)) {
        this.linkWorkItemData = element
        this.linkWorkItem()
      } else {
        this.showObjectiveModal = true
        await this.$nextTick()
        this.$refs.objectiveModal.openForm(
          OKR_FORM_VIEWS.OBJECTIVE,
          payload,
          'grid',
          false,
          editPayload
        )
      }
    },

    /** @public */
    deleteOkrElement(element) {
      this.deleteObjectiveData = element
      this.isConfirmDeleteShow = true
    },

    /** @public */
    async duplicateOkrElement(okrElement) {
      this.showObjectiveModal = true
      await this.$nextTick()

      this.$refs.objectiveModal.openForm(
        OKR_FORM_VIEWS.DUPLICATE_OBJECTIVE,
        {
          typeId: okrElement.typeId,
          levelId: okrElement.levelId,
          intervalId: okrElement.intervalId,
          parentId: okrElement.parentId,
          contribute: okrElement.contribute,
          objectiveId: okrElement.id,
          name: okrElement.name,
          isCanCopy: currentUserCanCopyObjective(okrElement)
        },
        'grid'
      )
    },

    /** @public */
    async editOkrElementWeights(okrElement) {
      this.showObjectiveModal = true
      await this.$nextTick()

      this.$refs.objectiveModal.openForm(
        OKR_FORM_VIEWS.CUSTOM_WEIGHTS,
        {
          id: okrElement.id,
          typeId: okrElement.typeId,
          intervalId: okrElement.intervalId,
          name: `${okrElement.displayId} ${okrElement.name}`,
          workspaceId: okrElement.workspaceId
        },
        'grid'
      )
    },

    /** @public */
    openCheckinModal(payload) {
      this.handleOpenCheckinModal(payload)
    }
  }
})
</script>

<style></style>
